import { useState } from "react";
import { includes } from "lodash";
import { useRouter } from "next/router";

import { Seo, Status, Select, Layout } from "~components";
import {
  usePermissions,
  useUser,
  useSuperAdmin,
  useOrganization,
  useIsMobile,
} from "~hooks";

import { NAV_ITEMS } from "~components/common/Navbar";
import { Card, OverviewSummary, EventSummaryCard } from "~components/reports";
import { OrderByDirection, Role } from "~graphql/sdk";
import { withAuth } from "~lib/helpers";
import { useQuery } from "~hooks/useQuery";
import { DashboardEventsDocument } from "~graphql/typed-document-nodes";
import { Box } from "flicket-ui";
import useScreenSize from "~hooks/useScreenSize";

const Home = () => {
  const router = useRouter();
  const [selectedEventId, setSelectedEventId] = useState("");
  const { user, isLoggedIn, isEventManager } = useUser();
  const { hasPermissions, Permission } = usePermissions();
  const { isSuperAdmin, isSuperAdminDomain } = useSuperAdmin();
  const { hasFeature } = useOrganization();
  const isMobile = useScreenSize().isTabletPortraitDown;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data: eventsData, error: eventsError, isLoading } = useQuery(
    DashboardEventsDocument,
    {
      where: {
        startDate: today.toISOString(),
        endDate: undefined,
      },
      orderBy: { startDate: OrderByDirection.Asc },
    }
  );

  const events = eventsData?.events?.edges?.map(({ node }) => node);

  if (isSuperAdmin && isSuperAdminDomain) {
    router.replace("/accounts").catch((e) => console.error(e));
    return null;
  } else if (includes(user?.roles, Role.PosAdmin) && user?.roles.length === 1) {
    router.replace("/pos").catch((e) => console.error(e));
    return null;
  }

  if (!isLoggedIn) {
    router.replace("/login").catch((e) => console.error(e));
    return null;
  }

  if (!hasPermissions(Permission.ReportingFinancial) || isEventManager) {
    const firstRoute = NAV_ITEMS({ hasPermissions, user, hasFeature })[0];
    router.replace(firstRoute.url).catch((e) => console.error(e));
    return null;
  }

  const selectedEvents =
    selectedEventId === ""
      ? events
      : events?.filter((event) => selectedEventId === event.id);

  const options = events?.map((event) => ({
    label: event.title,
    value: event.id,
  }));
  options?.unshift({ label: "All Events", value: "" });

  return (
    <>
      <Seo title="Flicket | Dashboard" description="Flicket dashboard" />

      <Layout title="Home" description="Home page">
        <>
          <OverviewSummary />

          <Box mb={4} />

          <Card title="Upcoming events">
            <Status
              loading={!eventsError && !events && !isLoading}
              error={eventsError}
            >
              {!isMobile &&
                events?.map((event) => (
                  <EventSummaryCard key={event.id} event={event} />
                ))}

              {isMobile && events && (
                <Select
                  pb={4}
                  isMulti={false}
                  name="event-selector"
                  options={options}
                  onChange={setSelectedEventId}
                  value={selectedEventId}
                  small
                />
              )}

              {isMobile &&
                selectedEvents &&
                selectedEvents.map((selectedEvent) => (
                  <EventSummaryCard
                    key={selectedEvent.id}
                    event={selectedEvent}
                  />
                ))}
            </Status>
          </Card>
        </>
      </Layout>
    </>
  );
};

export default withAuth(Home);
